import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAuth } from '../context/AuthContext';
import { ProtectedRoute } from '../components/ProtectedRoute';
import AdminLayout from '../layouts/AdminLayout';
import { fetchPublicFeatureFlags } from '../services/featureFlagsApi';
import { getDevLoyaltyEnabledOverride } from '../utils/devOverrides';
import { FEATURE_FLAGS, isPublicFeatureFlagEnabled } from '../constants/featureFlags';
import PageLoader from '../components/feedback/PageLoader';
import { useStaffFeatureFlagAccess } from '../hooks/useStaffFeatureFlagAccess';

export const ProtectedRouteWrapper = ({ children, requireAdmin = false, requireWaiter = false }) => (
  <ProtectedRoute requireAdmin={requireAdmin} requireWaiter={requireWaiter}>
    <AdminLayout>{children}</AdminLayout>
  </ProtectedRoute>
);

export const LoyaltyFeatureGuard = ({ children }) => {
  const [isEnabled, setIsEnabled] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const devOverride = getDevLoyaltyEnabledOverride();
        if (devOverride !== null) {
          if (mounted) {
            setIsEnabled(devOverride);
            setLoading(false);
          }
          return;
        }

        const flags = await fetchPublicFeatureFlags();
        if (!mounted) return;
        const loyaltyFlag = Array.isArray(flags)
          ? flags.find((f) => f.key === FEATURE_FLAGS.LOYALTY_ENABLED)
          : null;
        setIsEnabled(loyaltyFlag?.value === true || loyaltyFlag?.enabled === true);
      } catch {
        if (mounted) setIsEnabled(false);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return <PageLoader minHeight="100vh" />;
  }

  if (!isEnabled) {
    return <Navigate to="/" replace />;
  }

  return children;
};

export const LoyaltyReservationsFeatureGuard = ({ children }) => {
  const [isEnabled, setIsEnabled] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const flags = await fetchPublicFeatureFlags();
        if (!mounted) return;
        setIsEnabled(isPublicFeatureFlagEnabled(flags, FEATURE_FLAGS.LOYALTY_USER_RESERVATIONS));
      } catch {
        if (mounted) setIsEnabled(false);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return <PageLoader minHeight="100vh" />;
  }

  if (!isEnabled) {
    return <Navigate to="/loyalty/profile" replace />;
  }

  return children;
};

/** Staff reservation queue: enabled for admins even when the flag is off. */
export const AdminReservationsFeatureGuard = ({ children }) => {
  const { allowed, loading } = useStaffFeatureFlagAccess(FEATURE_FLAGS.LOYALTY_USER_RESERVATIONS);

  if (loading) {
    return <PageLoader minHeight="100vh" />;
  }

  if (!allowed) {
    return <Navigate to="/admin/access-denied" replace />;
  }

  return children;
};

export const RoleBasedRedirect = () => {
  const { roles } = useSelector((state) => state.auth);
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  if (roles?.includes('waiter')) {
    return <Navigate to="/admin/scan-qr" replace />;
  }

  return <Navigate to="/admin/hookah-flavors" replace />;
};
